/**
 * NoteCreateActionCard — read/edit view for ahoy_note create actions.
 *
 * Renders NoteCard with a synthesized Note from the action payload. Edit is
 * handled internally by NoteCard (the accordion Edit button is suppressed for
 * notes via isEditableAction). onEdit saves the updated values back to the
 * proposal action payload instead of the CRM store.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { NoteCard } from '@/lib/crm/record/notes/NoteCard';
import type { Note } from '@/lib/crm/record/notes/notes.types';
import {
  dueProperties,
  getMemoAssociationNames,
  isMemoEntityType,
  type MemoEntityType,
} from '@/lib/crm/record/notes/notes.utils';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import {
  extractProperties,
  formatValue,
  wrapProperties,
} from './CreateActionCard.helpers';
import { useActionPayloadSave } from './shared';

// Stable empty-array identity so the default `associations` prop doesn't
// change reference each render (keeps the synthesizeNote useMemo stable).
const EMPTY_ASSOCIATIONS: MergedAssociation[] = [];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Which note-like type a create action proposes. The payload's
 * `entity_type_name` is `ahoy_note` or `ahoy_task`; anything else can't reach
 * this card (CreateActionCard / MergedCreateActionCard dispatch here only for
 * note-like types), so the fallback is defensive.
 */
function resolveEntityType(
  action: ProposedAction<ObjectCreatedEntityPayload>
): MemoEntityType {
  const name = action.payload.entity_type_name;
  return isMemoEntityType(name) ? name : 'ahoy_note';
}

/**
 * Build a synthetic Note from an ahoy_note / ahoy_task create-action payload
 * (plus its folded-in association siblings) so we can pass it directly to
 * NoteCard. The record doesn't exist in the CRM yet, so entity_id is an empty
 * string and timestamps are blank — NoteCard handles these gracefully (no
 * attribution row, no edit/delete timestamps).
 *
 * Owner and stage live in sibling `ahoy_association_added` actions (the
 * ObjectCreated payload only carries properties), so they arrive here via
 * `associations` — the merged associations absorbed into this card. Their
 * association names are the type's own (`ahoy_task_to_owner` for a task).
 */
function synthesizeNote(
  action: ProposedAction<ObjectCreatedEntityPayload>,
  associations: MergedAssociation[],
  entityType: MemoEntityType
): Note {
  const flatProps = extractProperties(action.payload.payload.properties);
  const stageAssoc = associations.find(
    a => a.associationType === getMemoAssociationNames(entityType).stage
  );
  return {
    entity_id: '',
    // Leave empty when the proposal payload has no title so the user is never
    // shown a placeholder string ('Note') that could be saved verbatim as the
    // actual title. NoteCard hides the title row when empty, matching the
    // canonical MemoForm initial-title contract.
    title: formatValue(flatProps['ahoy_title']) || '',
    content: formatValue(flatProps['ahoy_content']),
    created_at: '',
    updated_at: '',
    // Intentionally left null: a populated owner_entity_id would pre-fill
    // MemoForm's owner picker with the wrong user when editing a synthesized
    // note. The owner is surfaced for display via the `ownerName` prop instead.
    owner_entity_id: null,
    last_event_seq: 0,
    // Due date — tasks only. `ahoy_note` no longer carries ahoy_date/
    // ahoy_datetime (see #13931), so a note payload never has one to surface;
    // `ahoy_task` keeps both. A task can carry ahoy_date alone (date-only due)
    // or both ahoy_date and ahoy_datetime together (timed due, where ahoy_date
    // holds the local calendar date ahoy_datetime falls on so the date-only
    // view never goes stale). NoteCard prefers datetime when present and
    // formats whichever is set.
    date:
      entityType === 'ahoy_task'
        ? formatValue(flatProps['ahoy_date']) || undefined
        : undefined,
    datetime:
      entityType === 'ahoy_task'
        ? formatValue(flatProps['ahoy_datetime']) || undefined
        : undefined,
    stage_entity_id: stageAssoc?.targetEntityId ?? null,
    creator_entity_id: null,
    is_private: false,
  };
}

/**
 * Resolve the note owner's display name from the folded-in owner association.
 * The backend enriches association actions with the target entity's label, so
 * we read it straight off `enrichedData` rather than re-fetching the user.
 */
function resolveOwnerName(
  associations: MergedAssociation[],
  entityType: MemoEntityType
): string | undefined {
  const ownerAssoc = associations.find(
    a => a.associationType === getMemoAssociationNames(entityType).owner
  );
  return (
    ownerAssoc?.enrichedData?.target_object?.label ??
    ownerAssoc?.enrichedData?.object_label ??
    undefined
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface NoteCreateActionCardProps {
  action: ProposedAction<ObjectCreatedEntityPayload>;
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
  /**
   * Association siblings folded into this note's card (owner, stage, parent
   * link, etc.). Owner and stage are rendered inside NoteCard; the rest are
   * intentionally not shown. Empty in non-merged render paths.
   */
  associations?: MergedAssociation[];
}

/**
 * Renders the note preview (NoteCard) for an ahoy_note create action.
 *
 * Edit mode is managed by NoteCard internally — clicking the pencil opens
 * MemoForm in-place. onEdit saves back to the proposal action payload (not
 * the CRM), so the same MemoForm used in the slideout drives proposal edits
 * with no duplication.
 */
export function NoteCreateActionCard({
  action,
  proposalId,
  orgId,
  onUpdate,
  associations = EMPTY_ASSOCIATIONS,
}: NoteCreateActionCardProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const entityType = resolveEntityType(action);
  const note = useMemo(
    () => synthesizeNote(action, associations, entityType),
    [action, associations, entityType]
  );
  const ownerName = useMemo(
    () => resolveOwnerName(associations, entityType),
    [associations, entityType]
  );

  const handleEdit = useCallback(
    async (
      _noteEntityId: string,
      title: string,
      content: string,
      opts?: {
        date?: string;
        ownerEntityId?: string;
        stageAssociation?: string | null;
        privacy?: 'private' | 'shared';
      }
    ) => {
      // Stage edits aren’t persisted to the proposal action payload — the
      // action is a single ahoy_object_created event whose payload schema
      // doesn’t carry associations. Stage changes would need a separate
      // proposed action (ahoy_association_added), which the proposal-action
      // editor doesn’t support inline. Accept the argument so it’s not
      // silently dropped against the form’s typed contract, then surface a
      // toast when the submitted stage actually differs from the one folded
      // into this card.
      const { date, stageAssociation } = opts ?? {};
      if (!proposalId || !orgId) {
        return;
      }
      // Warn only on a real stage change. A task’s folded-in stage round-trips
      // through the form unchanged on every save, so comparing against it keeps
      // a plain title/body/due-date edit silent instead of toasting each time.
      // The copy is task-specific: a task keeps its due date (saved below), so
      // the note wording — which says only title and body were kept — would
      // wrongly imply a successful due-date edit had been dropped.
      const submittedStage = stageAssociation ?? null;
      const stageChanged = submittedStage !== (note.stage_entity_id ?? null);
      setIsSaving(true);
      setSaveError(null);
      try {
        const baseProps = extractProperties(action.payload.payload.properties);
        // Due date round-trips for tasks only — `ahoy_note` no longer carries
        // ahoy_date/ahoy_datetime (see #13931), and MemoForm hides its date
        // picker for notes, so `date` is always undefined for a note edit.
        // For a task: strip BOTH due-property keys from baseProps before
        // re-spreading — the form emits a single due string, and whichever
        // property it doesn't map to must be cleared so a stale value can't
        // re-persist (toggling a date-only due to a timed due, or vice versa,
        // would otherwise leave the previous property in the payload).
        const {
          ahoy_date: _baseDate,
          ahoy_datetime: _baseDatetime,
          ...basePropsWithoutDue
        } = baseProps;
        // Map the form's single due string back to the ahoy_date / ahoy_datetime
        // pair — mirroring how useMemoRecords.updateRecord writes the pair. A
        // YYYY-MM-DD value sets only ahoy_date; an ISO string with "T" sets
        // only ahoy_datetime — deliberately unpaired: an instant has no
        // calendar day until a timezone is chosen, so readers collapse
        // ahoy_datetime to a day at read time in the viewer's timezone. An
        // empty/undefined value omits both from this create payload (in a
        // create payload "cleared" and "absent" are the same thing).
        const due =
          entityType === 'ahoy_task'
            ? dueProperties(date)
            : { ahoy_date: null, ahoy_datetime: null };
        const updatedProperties = wrapProperties({
          ...basePropsWithoutDue,
          ahoy_title: title,
          ahoy_content: content,
          ...(due.ahoy_date != null && { ahoy_date: due.ahoy_date }),
          ...(due.ahoy_datetime != null && {
            ahoy_datetime: due.ahoy_datetime,
          }),
        });
        const updatedPayload: ObjectCreatedEntityPayload = {
          ...action.payload,
          payload: {
            ...action.payload.payload,
            properties: updatedProperties,
          },
        };
        await savePayload(updatedPayload);
        // Surface the dropped-stage-change warning only after the save
        // succeeds — the copy asserts the other details "were saved", so
        // emitting it before savePayload would lie to the user on failure.
        if (stageChanged) {
          toast.message(
            entityType === 'ahoy_task'
              ? 'Stage changes aren’t saved in proposals yet. The task’s other details were saved.'
              : 'Converting a note to a task isn’t supported in proposals yet. Only the note title and body were saved.'
          );
        }
      } catch (err) {
        setSaveError(err instanceof Error ? err : new Error('Save failed'));
      } finally {
        setIsSaving(false);
      }
    },
    [action, entityType, note.stage_entity_id, proposalId, orgId, savePayload]
  );

  // Readonly contexts (mode === 'readonly' / no editing) omit `onUpdate`.
  // Withhold `onEdit` in that case so NoteCard hides its edit pencil — the
  // accordion-level edit gating already removes the shell Edit button for
  // every memo entity (`isEditableAction` excludes both `ahoy_note` and
  // `ahoy_task` via `isMemoEntityType`), but NoteCard's internal pencil would
  // otherwise let users open the form and attempt a save that has no proposal
  // update plumbing.
  //
  // Also gate on proposalId / orgId being present: handleEdit short-circuits
  // when either is missing, so without this check the user could enter edit
  // mode and submit changes that silently no-op. Withholding onEdit hides
  // the pencil entirely in that case.
  const canEdit = Boolean(onUpdate && proposalId && orgId);
  // Bail out cleanly when no org context is available. The card relies on
  // `orgId` downstream for entity-mention hydration (MarkdownContent) and
  // stage deep-links (NoteStageLink); passing `0` would drive org-scoped
  // rendering / requests against an invalid org. In practice `orgId` is
  // always set by the proposal renderer, so this branch is defensive.
  if (!orgId) {
    return null;
  }
  return (
    <NoteCard
      note={note}
      orgId={orgId}
      entityType={entityType}
      ownerName={ownerName}
      canDelete={false}
      onEdit={canEdit ? handleEdit : undefined}
      isEditing={isSaving}
      editError={saveError}
      // A synthesized note has no real created/updated timestamps, so keep the
      // "created/edited X ago" attribution line hidden — showing it for a
      // proposal would be a meaningless "Unknown" row.
      showAttribution={false}
      // ...but DO show the bottom footer so the note's due date and owner are
      // surfaced (folded in from the proposal's sibling association actions).
      // The footer auto-hides when there's neither a date nor an owner.
      showFooter
    />
  );
}
